/**
 * Espelho de Ponto Mensal
 *
 * Visualização completa do mês: marcações originais, pares Ent/Sai,
 * horas calculadas com regras CLT, DSR semanal, tratamentos e totais.
 *
 * Código de cores:
 *   Vermelho  — número ímpar de marcações (falta de marcação)
 *   Amarelo   — falta / atraso / sábado trabalhado
 *   Azul      — domingo (DSR) ou folga
 *   Branco    — dia normal
 */
import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { espApi, funcApi } from '../../lib/api'
import { PageLoading } from '../../components/LoadingSpinner'
import { formatCpf } from '../../types/api'

// ── tipos locais ──────────────────────────────────────────────────────────────

interface DiaFolha {
  dia: number; dia_semana: string; is_dom: boolean; is_sab: boolean
  marc_originais: string[]
  ent1: string | null; sai1: string | null
  ent2: string | null; sai2: string | null
  ent3: string | null; sai3: string | null
  total_s: number; extra_s: number; falta_s: number; atraso_s: number; dsr_s: number
  cor: 'NORMAL' | 'VERMELHO' | 'AMARELO' | 'AZUL'
  ch: number
  ocorrencia: string | null; hora_ocorrencia: string | null; motivo: string | null
  status_espelho: string | null
}

interface FolhaMensal {
  funcionario: { cpf_pis: number; nome: string; cargo: string; departamento: string; jornada_s: number }
  periodo: { ano: number; mes: number; num_dias: number }
  dias: DiaFolha[]
  totais: { normais_s: number; extras_s: number; extra_50_s: number; extra_100_s: number; faltas_s: number; dsr_s: number }
  semanais: { semana: number; normais_s: number; dsr_s: number }[]
  clt_version: string
}

// ── helpers ───────────────────────────────────────────────────────────────────

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro']

function fmt(s: number | null | undefined): string {
  if (!s || s <= 0) return ''
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`
}

function rowBg(cor: DiaFolha['cor']): string {
  if (cor === 'VERMELHO') return '#fce8e8'
  if (cor === 'AMARELO')  return '#fff8dc'
  if (cor === 'AZUL')     return '#d0eaf8'
  return '#ffffff'
}

function rowTextColor(cor: DiaFolha['cor'], is_sab: boolean): string {
  if (cor === 'AZUL') return '#1a5f8a'
  if (is_sab) return '#555'
  return '#222'
}

// ── componente principal ──────────────────────────────────────────────────────

export function EspelhoMensalPage() {
  const now = new Date()
  const qc = useQueryClient()
  const [cpfPis, setCpfPis] = useState<number | null>(null)
  const [ano, setAno] = useState(now.getFullYear())
  const [mes, setMes] = useState(now.getMonth() + 1)
  const [recalcMsg, setRecalcMsg] = useState<string | null>(null)
  const printRef = useRef<HTMLDivElement>(null)

  const { data: funcs } = useQuery({ queryKey: ['funcionarios', true], queryFn: () => funcApi.list(true) })

  const { data: folha, isLoading, isFetching } = useQuery<FolhaMensal>({
    queryKey: ['folha-mensal', cpfPis, ano, mes],
    queryFn: () => espApi.folhaMensal(cpfPis!, ano, mes) as Promise<FolhaMensal>,
    enabled: cpfPis !== null,
  })

  // Recalcula todos os espelhos do mês para o funcionário (dia a dia).
  const recalcMutation = useMutation({
    mutationFn: async () => {
      if (!cpfPis) return
      const daysInMonth = new Date(ano, mes, 0).getDate()
      const promises: Promise<unknown>[] = []
      for (let d = 1; d <= daysInMonth; d++) {
        const data = `${ano}-${String(mes).padStart(2,'0')}-${String(d).padStart(2,'0')}`
        promises.push(espApi.calcular(cpfPis, data).catch(() => null))
      }
      await Promise.all(promises)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folha-mensal', cpfPis, ano, mes] })
      setRecalcMsg('Espelho recalculado.')
      setTimeout(() => setRecalcMsg(null), 3000)
    },
  })

  function navMes(delta: number) {
    let m = mes + delta; let a = ano
    if (m < 1) { m = 12; a-- }
    if (m > 12) { m = 1; a++ }
    setMes(m); setAno(a)
  }

  function handlePrint() { window.print() }

  const f = folha?.funcionario
  const totais = folha?.totais

  return (
    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#222' }}>

      {/* ── Barra de Controles ─────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap', backgroundColor: '#f0f4f8', padding: '10px 14px', borderRadius: 6, border: '1px solid #d0dae6' }}>
        <button
          style={{ backgroundColor: recalcMutation.isPending ? '#5dade2' : '#2980b9', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 16px', cursor: cpfPis ? 'pointer' : 'not-allowed', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6, opacity: cpfPis ? 1 : 0.5 }}
          onClick={() => { if (cpfPis) { setRecalcMsg(null); recalcMutation.mutate() } }}
          disabled={recalcMutation.isPending || !cpfPis}
        >
          {recalcMutation.isPending ? '↻ Calculando…' : '↻ RECALCULAR'}
        </button>
        {recalcMsg && <span style={{ fontSize: 12, color: '#27ae60', fontWeight: 600 }}>{recalcMsg}</span>}

        {/* Seletor de funcionário */}
        <select
          style={{ padding: '5px 10px', borderRadius: 4, border: '1px solid #bbb', minWidth: 220 }}
          value={cpfPis ?? ''}
          onChange={e => setCpfPis(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">— Selecione o funcionário —</option>
          {funcs?.map(f => (
            <option key={f.id} value={f.cpf}>{f.nome} — {formatCpf(f.cpf)}</option>
          ))}
        </select>

        {/* Período */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <select
            style={{ padding: '5px 8px', borderRadius: 4, border: '1px solid #bbb' }}
            value={mes}
            onChange={e => setMes(Number(e.target.value))}
          >
            {MESES.map((_m, i) => <option key={i} value={i+1}>{String(i+1).padStart(2,'0')}/{ano}</option>)}
          </select>
          <input
            type="number" min={2000} max={2099}
            value={ano}
            onChange={e => setAno(Number(e.target.value))}
            style={{ width: 70, padding: '5px 8px', borderRadius: 4, border: '1px solid #bbb' }}
          />
        </div>

        {/* Navegação mês */}
        <div style={{ display: 'flex', gap: 2 }}>
          <NavBtn onClick={() => navMes(-12)}>«</NavBtn>
          <NavBtn onClick={() => navMes(-1)}>‹</NavBtn>
          <NavBtn onClick={() => navMes(1)}>›</NavBtn>
          <NavBtn onClick={() => navMes(12)}>»</NavBtn>
        </div>

        {/* Ações */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <ActionBtn onClick={handlePrint}>🖨</ActionBtn>
        </div>
      </div>

      {!cpfPis ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#888' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📋</div>
          <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 8 }}>Selecione um funcionário</div>
          <div style={{ fontSize: 13 }}>Escolha o funcionário no seletor acima para visualizar o espelho de ponto.</div>
        </div>
      ) : isLoading || isFetching ? (
        <PageLoading />
      ) : !folha ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#e74c3c' }}>Erro ao carregar o espelho.</div>
      ) : (
        <div ref={printRef}>

          {/* ── Cabeçalho do espelho ─────────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>{f?.nome}</div>
              <div style={{ color: '#555', fontSize: 12 }}>Departamento: {f?.departamento || '—'}</div>
              <div style={{ color: '#555', fontSize: 12 }}>Cargo: {f?.cargo || '—'} | CPF: {formatCpf(f?.cpf_pis ?? 0)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 13 }}>
                Jornada de 01/{String(mes).padStart(2,'0')}/{ano} à {folha.periodo.num_dias}/{String(mes).padStart(2,'0')}/{ano}
              </div>
              <div style={{ fontSize: 12, color: '#555' }}>{MESES[mes-1]} de {ano} | Regra CLT: {folha.clt_version}</div>
            </div>
            <div style={{ backgroundColor: '#fff8e1', border: '1px solid #ffd54f', borderRadius: 4, padding: '6px 12px', fontSize: 12 }}>
              <strong>Tratamentos</strong><br/>
              <button style={{ marginTop: 6, backgroundColor: '#f39c12', color: '#fff', border: 'none', borderRadius: 3, padding: '4px 10px', cursor: 'pointer', fontSize: 11 }}>
                ✏ AVALIAÇÃO PENDENTE
              </button>
            </div>
          </div>

          {/* ── Tabela principal ─────────────────────────────────────────────── */}
          <div style={{ overflowX: 'auto', border: '1px solid #c0cdd8', borderRadius: 4 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', minWidth: 900 }}>
              <colgroup>
                <col style={{ width: 60 }} />   {/* Dia */}
                <col style={{ width: 150 }} />  {/* Marc. Orig. */}
                <col style={{ width: 42 }} />   {/* Ent1 */}
                <col style={{ width: 42 }} />   {/* Sai1 */}
                <col style={{ width: 42 }} />   {/* Ent2 */}
                <col style={{ width: 42 }} />   {/* Sai2 */}
                <col style={{ width: 42 }} />   {/* Ent3 */}
                <col style={{ width: 42 }} />   {/* Sai3 */}
                <col style={{ width: 46 }} />   {/* Total */}
                <col style={{ width: 46 }} />   {/* Extra */}
                <col style={{ width: 46 }} />   {/* Falta */}
                <col style={{ width: 42 }} />   {/* Atraso */}
                <col style={{ width: 30 }} />   {/* CH */}
                <col style={{ width: 44 }} />   {/* Hora */}
                <col style={{ width: 32 }} />   {/* Ocor */}
                <col />                          {/* Motivo */}
              </colgroup>

              <thead>
                <tr style={{ backgroundColor: '#2c3e50', color: '#fff' }}>
                  <Th>Dia</Th>
                  <Th>Marcações Originais</Th>
                  <Th>Ent 1</Th>
                  <Th>Sai 1</Th>
                  <Th>Ent 2</Th>
                  <Th>Sai 2</Th>
                  <Th>Ent 3</Th>
                  <Th>Sai 3</Th>
                  <Th>Total</Th>
                  <Th style={{ color: '#2ecc71' }}>Extra</Th>
                  <Th style={{ color: '#e74c3c' }}>Falta</Th>
                  <Th>Atraso</Th>
                  <Th>CH</Th>
                  <Th>Hora</Th>
                  <Th>Ocor.</Th>
                  <Th style={{ textAlign: 'left', paddingLeft: 8 }}>Motivo</Th>
                </tr>
              </thead>

              <tbody>
                {folha.dias.map(d => {
                  const bg = rowBg(d.cor)
                  const tc = rowTextColor(d.cor, d.is_sab)
                  const isBold = d.is_dom || d.is_sab
                  const borderB = d.is_dom ? '2px solid #a0bcd0' : '1px solid #e0e8ef'

                  return (
                    <tr key={d.dia} style={{ backgroundColor: bg, color: tc, borderBottom: borderB }}>
                      {/* Dia */}
                      <td style={{ padding: '3px 6px', fontWeight: isBold ? 700 : 500, whiteSpace: 'nowrap' }}>
                        {String(d.dia).padStart(2,'0')} {d.dia_semana}
                      </td>

                      {/* Marcações Originais */}
                      <td style={{ padding: '3px 6px', fontSize: 12, color: d.cor === 'VERMELHO' ? '#c0392b' : tc }}>
                        {d.marc_originais.join('  ')}
                      </td>

                      {/* Pares Ent/Sai */}
                      {(['ent1','sai1','ent2','sai2','ent3','sai3'] as const).map(k => (
                        <td key={k} style={{ padding: '3px 4px', textAlign: 'center', fontSize: 12, color: d[k] ? tc : '#bbb' }}>
                          {d[k] ?? ''}
                        </td>
                      ))}

                      {/* Total */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', fontWeight: 600, color: d.total_s > 0 ? '#27ae60' : '#bbb' }}>
                        {fmt(d.total_s)}
                      </td>

                      {/* Extra */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', color: '#2980b9', fontWeight: d.extra_s > 0 ? 600 : 400 }}>
                        {fmt(d.extra_s)}
                      </td>

                      {/* Falta */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', color: '#e74c3c', fontWeight: d.falta_s > 0 ? 600 : 400 }}>
                        {fmt(d.falta_s)}
                      </td>

                      {/* Atraso */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', color: '#e67e22' }}>
                        {fmt(d.atraso_s)}
                      </td>

                      {/* CH */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {d.marc_originais.length > 0 || d.dsr_s > 0 ? d.ch : ''}
                      </td>

                      {/* Hora ocorrência */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: 12, color: '#7f8c8d' }}>
                        {d.hora_ocorrencia ?? ''}
                      </td>

                      {/* Ocor. */}
                      <td style={{ padding: '3px 4px', textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#8e44ad' }}>
                        {d.ocorrencia ?? ''}
                      </td>

                      {/* Motivo */}
                      <td style={{ padding: '3px 8px', fontSize: 11, color: '#7f8c8d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.motivo ?? ''}
                      </td>
                    </tr>
                  )
                })}

                {/* Linha de totais */}
                <tr style={{ backgroundColor: '#2c3e50', color: '#fff', fontWeight: 700, borderTop: '2px solid #1a252f' }}>
                  <td colSpan={8} style={{ padding: '5px 8px', textAlign: 'right' }}>Total do período</td>
                  <td style={{ padding: '5px 4px', textAlign: 'center', color: '#2ecc71' }}>{fmt(totais?.normais_s)}</td>
                  <td style={{ padding: '5px 4px', textAlign: 'center', color: '#3498db' }}>{fmt(totais?.extras_s)}</td>
                  <td style={{ padding: '5px 4px', textAlign: 'center', color: '#e74c3c' }}>{fmt(totais?.faltas_s)}</td>
                  <td colSpan={5} style={{ padding: '5px 4px', textAlign: 'center', color: '#bdc3c7', fontSize: 11 }}>
                    DSR: {fmt(totais?.dsr_s)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* ── Legenda de Linhas ─────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginTop: 10, fontSize: 12, color: '#555' }}>
            <span style={{ fontWeight: 600 }}>Legenda de Linhas:</span>
            <LegItem color="#fce8e8" border="#e74c3c">Número ímpar de marcações</LegItem>
            <LegItem color="#fff8dc" border="#f39c12">Falta</LegItem>
            <LegItem color="#d0eaf8" border="#2980b9">Folga / DSR</LegItem>
            <LegItem color="#ffffff" border="#bbb">Normal</LegItem>
          </div>

          {/* ── Painéis de totais ─────────────────────────────────────────────── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'auto auto 1fr auto', gap: 16, marginTop: 20, alignItems: 'start' }}>

            {/* Hora extra Mensal */}
            <SummaryPanel title="Hora extra Mensal">
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                <thead>
                  <tr style={{ backgroundColor: '#ecf0f1' }}>
                    <th style={thS}>Período</th>
                    <th style={thS}>%</th>
                    <th style={thS}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={tdS}>Seg a sábado</td>
                    <td style={tdS}>50</td>
                    <td style={{ ...tdS, color: '#2980b9', fontWeight: 600 }}>{fmt(totais?.extra_50_s) || '00:00'}</td>
                  </tr>
                  {(totais?.extra_100_s ?? 0) > 0 && (
                    <tr>
                      <td style={tdS}>Acima de 2h</td>
                      <td style={tdS}>100</td>
                      <td style={{ ...tdS, color: '#2980b9', fontWeight: 600 }}>{fmt(totais?.extra_100_s)}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </SummaryPanel>

            {/* D.S.R. */}
            <SummaryPanel title="D.S.R.">
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                <tbody>
                  <tr>
                    <td style={{ ...tdS, color: '#27ae60', fontWeight: 600 }}>D.S.R.(+):</td>
                    <td style={tdS}>{fmt(totais?.dsr_s) || '00:00'}</td>
                  </tr>
                  <tr>
                    <td style={{ ...tdS, color: '#e74c3c' }}>D.S.R.(-):</td>
                    <td style={tdS}>00:00</td>
                  </tr>
                  <tr>
                    <td style={tdS}>Quantia (+):</td>
                    <td style={tdS}>{folha.semanais.filter(s => s.dsr_s > 0).length}</td>
                  </tr>
                </tbody>
              </table>
            </SummaryPanel>

            {/* Totais Diferenciados */}
            <SummaryPanel title="Totais Diferenciados">
              <table style={{ borderCollapse: 'collapse', fontSize: 12, width: '100%' }}>
                <tbody>
                  <DiffRow label="Horas Trab. (+) D.S.R.:" value={fmt((totais?.normais_s ?? 0) + (totais?.dsr_s ?? 0))} color="#27ae60" />
                  <DiffRow label="Horas Trab. (-) Extras:" value={fmt(Math.max(0, (totais?.normais_s ?? 0) - (totais?.extras_s ?? 0)))} />
                  <DiffRow label="Horas Abonadas" value="00:00" />
                  <DiffRow label="Dias de Falta integral:" value={`${Math.floor((totais?.faltas_s ?? 0) / ((folha.funcionario.jornada_s) || 28800))}`} />
                  <DiffRow label="Horas de Falta integral:" value={fmt(totais?.faltas_s)} color="#e74c3c" />
                  <DiffRow label="Horas de Falta parcial:" value="00:00" color="#e74c3c" />
                </tbody>
              </table>
            </SummaryPanel>

            {/* Totais Semanais */}
            <SummaryPanel title="Totais Semanais">
              <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {folha.semanais.map(s => (
                    <tr key={s.semana}>
                      <td style={{ ...tdS, paddingRight: 16 }}>Semana {s.semana}</td>
                      <td style={{ ...tdS, color: '#27ae60', fontWeight: 600, textAlign: 'right' }}>{fmt(s.normais_s) || '00:00'}</td>
                    </tr>
                  ))}
                  {folha.semanais.length === 0 && (
                    <tr><td style={tdS} colSpan={2}>—</td></tr>
                  )}
                </tbody>
              </table>
            </SummaryPanel>
          </div>

        </div>
      )}
    </div>
  )
}

// ── sub-componentes ───────────────────────────────────────────────────────────

function Th({ children, style }: { children?: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <th style={{ padding: '5px 4px', textAlign: 'center', fontSize: 11, fontWeight: 700, borderRight: '1px solid #3d5166', whiteSpace: 'nowrap', ...style }}>
      {children}
    </th>
  )
}

function NavBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{ padding: '5px 9px', border: '1px solid #bbb', borderRadius: 3, backgroundColor: '#fff', cursor: 'pointer', fontSize: 14, lineHeight: 1 }}>
      {children}
    </button>
  )
}

function ActionBtn({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{ padding: '5px 10px', border: '1px solid #bbb', borderRadius: 3, backgroundColor: '#fff', cursor: 'pointer' }}>
      {children}
    </button>
  )
}

function LegItem({ color, border, children }: { color: string; border: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 16, height: 16, backgroundColor: color, border: `1.5px solid ${border}`, display: 'inline-block', borderRadius: 2 }} />
      {children}
    </span>
  )
}

function SummaryPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ border: '1px solid #c8d8e8', borderRadius: 4, overflow: 'hidden', fontSize: 12 }}>
      <div style={{ backgroundColor: '#34495e', color: '#fff', padding: '5px 10px', fontWeight: 700, fontSize: 12 }}>{title}</div>
      <div style={{ padding: '8px 10px', backgroundColor: '#f8fafc' }}>{children}</div>
    </div>
  )
}

function DiffRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <tr>
      <td style={{ ...tdS, color: color ?? '#333' }}>{label}</td>
      <td style={{ ...tdS, color: color ?? '#333', fontWeight: color ? 600 : 400, textAlign: 'right', paddingLeft: 20 }}>{value || '00:00'}</td>
    </tr>
  )
}

const thS: React.CSSProperties = { padding: '4px 8px', textAlign: 'center', fontSize: 11, fontWeight: 700, border: '1px solid #ddd' }
const tdS: React.CSSProperties = { padding: '3px 8px', border: '1px solid #eee' }
